/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AcquisitionChannel } from '@localstack/types';
import type { AdminCreateUserRequest } from '@localstack/types';
import type { AdminSearchResult } from '@localstack/types';
import type { AdminUpdateOrganizationRequest } from '@localstack/types';
import type { AdminUpdateUserRequest } from '@localstack/types';
import type { AdminUser } from '@localstack/types';
import type { AuditTrailQueryResult } from '@localstack/types';
import type { BillingChannel } from '@localstack/types';
import type { CreateOrganizationSubscriptionRequest } from '@localstack/types';
import type { CreditCard } from '@localstack/types';
import type { Discount } from '@localstack/types';
import type { Extension } from '@localstack/types';
import type { IdentityLogEvent } from '@localstack/types';
import type { LegalAcceptance } from '@localstack/types';
import type { LicenseAssignment } from '@localstack/types';
import type { Organization } from '@localstack/types';
import type { Plan } from '@localstack/types';
import type { Product } from '@localstack/types';
import type { SeatHolderActivity } from '@localstack/types';
import type { SetLicenseAssignmentsRequest } from '@localstack/types';
import type { SortDirection } from '@localstack/types';
import type { Subscription } from '@localstack/types';
import type { TaxDetails } from '@localstack/types';
import type { UsageInsightSortField } from '@localstack/types';
import type { UsageSummary } from '@localstack/types';
import type { User } from '@localstack/types';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class AdminService {
  /**
   * @param orgId
   * @param fromDate
   * @param toDate
   * @param searchString
   * @param sortCol
   * @param sortDir
   * @param page
   * @param pageSize
   * @returns SeatHolderActivity
   * @throws ApiError
   */
  public static adminGetUsageInsights(
    orgId: string,
    fromDate?: string,
    toDate?: string,
    searchString?: string,
    sortCol?: UsageInsightSortField,
    sortDir?: SortDirection,
    page?: number,
    pageSize?: number,
  ): CancelablePromise<Array<SeatHolderActivity>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/usage/info',
      query: {
        org_id: orgId,
        from_date: fromDate,
        to_date: toDate,
        search_string: searchString,
        sort_col: sortCol,
        sort_dir: sortDir,
        page: page,
        page_size: pageSize,
      },
    });
  }

  /**
   * @param userId
   * @param startTimestamp
   * @param queryId
   * @param endTimestamp
   * @returns AuditTrailQueryResult
   * @throws ApiError
   */
  public static getUserAuditTrail(
    userId: any,
    startTimestamp: number,
    queryId?: any,
    endTimestamp?: number,
  ): CancelablePromise<AuditTrailQueryResult> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/users/{user_id}/audit-trail',
      path: {
        user_id: userId,
      },
      query: {
        query_id: queryId,
        start_timestamp: startTimestamp,
        end_timestamp: endTimestamp,
      },
    });
  }

  /**
   * @param orgId
   * @param startTimestamp
   * @param queryId
   * @param endTimestamp
   * @returns AuditTrailQueryResult
   * @throws ApiError
   */
  public static getOrgAuditTrail(
    orgId: any,
    startTimestamp: number,
    queryId?: any,
    endTimestamp?: number,
  ): CancelablePromise<AuditTrailQueryResult> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/organizations/{org_id}/audit-trail',
      path: {
        org_id: orgId,
      },
      query: {
        query_id: queryId,
        start_timestamp: startTimestamp,
        end_timestamp: endTimestamp,
      },
    });
  }

  /**
   * @param extensionId
   * @param requestBody
   * @returns any
   * @throws ApiError
   */
  public static updateExtensionAdmin(
    extensionId: string,
    requestBody?: Extension,
  ): CancelablePromise<{
    pre_signed_url: string;
  }> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/v1/admin/extensions/{extension_id}',
      path: {
        extension_id: extensionId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @returns any
   * @throws ApiError
   */
  public static listFeatureFlags(): CancelablePromise<Array<Array<any>>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/feature-flags',
    });
  }

  /**
   * @param requestBody
   * @returns any The feature flag was toggled successfully.
   * @throws ApiError
   */
  public static toggleFeatureFlag(requestBody?: { path: string }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/feature-flags',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @returns any Platform restart has been triggered.
   * @throws ApiError
   */
  public static restartPlatformServices(): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/restart',
    });
  }

  /**
   * @returns AdminUser A list of admin users
   * @throws ApiError
   */
  public static listAdminUsers(): CancelablePromise<Array<AdminUser>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/admin-users',
    });
  }

  /**
   * @param requestBody
   * @returns AdminUser Admin user has been successfully added
   * @throws ApiError
   */
  public static addAdminUser(requestBody: { email: string }): CancelablePromise<AdminUser> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/admin-users',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param adminId
   * @param requestBody
   * @returns AdminUser Admin user permissions updated
   * @throws ApiError
   */
  public static updateAdminUserPermissions(
    adminId: string,
    requestBody: {
      permissions: Array<string>;
    },
  ): CancelablePromise<AdminUser> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/v1/admin/admin-users/{admin_id}/permissions',
      path: {
        admin_id: adminId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param adminId
   * @returns AdminUser Admin user has been successfully removed
   * @throws ApiError
   */
  public static removeAdminUser(adminId: string): CancelablePromise<AdminUser> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/v1/admin/admin-users/{admin_id}',
      path: {
        admin_id: adminId,
      },
    });
  }

  /**
   * @returns any
   * @throws ApiError
   */
  public static listResources(): CancelablePromise<{
    plans?: Array<Plan>;
    discounts: Array<Discount>;
    products: Array<Product>;
  }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/resources',
    });
  }

  /**
   * @param orgId
   * @returns Organization
   * @throws ApiError
   */
  public static getOrganization(orgId: any): CancelablePromise<Organization> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/organizations/{org_id}',
      path: {
        org_id: orgId,
      },
    });
  }

  /**
   * @param orgId
   * @param requestBody
   * @returns Organization
   * @throws ApiError
   */
  public static updateOrganization(
    orgId: any,
    requestBody?: AdminUpdateOrganizationRequest,
  ): CancelablePromise<Organization> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/v1/admin/organizations/{org_id}',
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param orgId
   * @returns Organization
   * @throws ApiError
   */
  public static removeOrganization(orgId: any): CancelablePromise<Organization> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/v1/admin/organizations/{org_id}',
      path: {
        org_id: orgId,
      },
    });
  }

  /**
   * @param requestBody
   * @returns Organization
   * @throws ApiError
   */
  public static createOrganization(requestBody?: Organization): CancelablePromise<Organization> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/organizations',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param orgId
   * @param currency
   * @returns Organization
   * @throws ApiError
   */
  public static syncOrganizationStripeAccount(orgId: string, currency: string): CancelablePromise<Organization> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/organizations/{org_id}/stripe',
      path: {
        org_id: orgId,
      },
      query: {
        currency: currency,
      },
    });
  }

  /**
   * @param userId
   * @returns User
   * @throws ApiError
   */
  public static getUser(userId: any): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/users/{user_id}',
      path: {
        user_id: userId,
      },
    });
  }

  /**
   * @param userId
   * @param requestBody
   * @returns User
   * @throws ApiError
   */
  public static updateUser(userId: any, requestBody?: AdminUpdateUserRequest): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/v1/admin/users/{user_id}',
      path: {
        user_id: userId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param userId
   * @returns User
   * @throws ApiError
   */
  public static removeUser(userId: any): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/v1/admin/users/{user_id}',
      path: {
        user_id: userId,
      },
    });
  }

  /**
   * @param userId
   * @param requestBody
   * @returns User
   * @throws ApiError
   */
  public static replaceUserEmail(
    userId: any,
    requestBody?: {
      email: string;
    },
  ): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/v1/admin/users/{user_id}/emails',
      path: {
        user_id: userId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param userId
   * @returns any
   * @throws ApiError
   */
  public static assignUserPassword(userId: any): CancelablePromise<{
    password: string;
  }> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/v1/admin/users/{user_id}/password',
      path: {
        user_id: userId,
      },
    });
  }

  /**
   * @param requestBody
   * @returns User
   * @throws ApiError
   */
  public static createUser(requestBody?: AdminCreateUserRequest): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/users',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param userId
   * @returns User
   * @throws ApiError
   */
  public static resendUserActivation(userId: any): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/users/{user_id}/resend',
      path: {
        user_id: userId,
      },
    });
  }

  /**
   * @param userId
   * @returns User
   * @throws ApiError
   */
  public static activateUser(userId: any): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/users/{user_id}/activate',
      path: {
        user_id: userId,
      },
    });
  }

  /**
   * @param userId
   * @returns User
   * @throws ApiError
   */
  public static resetUserPassword(userId: any): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/users/{user_id}/reset',
      path: {
        user_id: userId,
      },
    });
  }

  /**
   * @param userId
   * @returns Organization
   * @throws ApiError
   */
  public static listUserOrganizations(userId: any): CancelablePromise<Array<Organization>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/users/{user_id}/organizations',
      path: {
        user_id: userId,
      },
    });
  }

  /**
   * @param orgId
   * @returns Subscription
   * @throws ApiError
   */
  public static listOrganizationSubscriptions(orgId: any): CancelablePromise<Array<Subscription>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/organizations/{org_id}/subscriptions',
      path: {
        org_id: orgId,
      },
    });
  }

  /**
   * @param orgId
   * @param requestBody
   * @returns Subscription
   * @throws ApiError
   */
  public static createOrganizationSubscription(
    orgId: any,
    requestBody?: CreateOrganizationSubscriptionRequest,
  ): CancelablePromise<Subscription> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/organizations/{org_id}/subscriptions',
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param orgId
   * @param subscriptionId
   * @returns Subscription
   * @throws ApiError
   */
  public static getOrganizationSubscription(orgId: string, subscriptionId: string): CancelablePromise<Subscription> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/organizations/{org_id}/subscriptions/{subscription_id}',
      path: {
        org_id: orgId,
        subscription_id: subscriptionId,
      },
    });
  }

  /**
   * @param orgId
   * @param subscriptionId
   * @param requestBody
   * @returns Subscription
   * @throws ApiError
   */
  public static updateOrganizationSubscription(
    orgId: string,
    subscriptionId: string,
    requestBody?: {
      plan: string;
      discount?: string;
      payment_id?: string;
      seats?: number;
      volumes?: Record<string, any>;
      products?: Array<string>;
      promo_code?: string;
      billing_cycle_anchor?: any;
      proration_behavior?: string;
      /**
       * Optional custom renewal date as Unix timestamp
       */
      custom_renewal_date?: number;
      mrr?: number;
      mrr_private?: number;
      acquisition_channel?: AcquisitionChannel;
      billing_channel?: BillingChannel;
      hubspot_subscription_id?: string;
    },
  ): CancelablePromise<Subscription> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/v1/admin/organizations/{org_id}/subscriptions/{subscription_id}',
      path: {
        org_id: orgId,
        subscription_id: subscriptionId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param orgId
   * @param subId
   * @returns Subscription
   * @throws ApiError
   */
  public static cancelOrganizationSubscription(orgId: any, subId: any): CancelablePromise<Subscription> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/v1/admin/organizations/{org_id}/subscriptions/{sub_id}',
      path: {
        org_id: orgId,
        sub_id: subId,
      },
    });
  }

  /**
   * @param orgId
   * @param subId
   * @param requestBody
   * @returns Subscription
   * @throws ApiError
   */
  public static rescheduleOrganizationSubscription(
    orgId: any,
    subId: any,
    requestBody?: {
      cancel_at?: number;
      cancel_at_period_end?: boolean;
      reactivate?: boolean;
    },
  ): CancelablePromise<Subscription> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/organizations/{org_id}/subscriptions/{sub_id}/reschedule',
      path: {
        org_id: orgId,
        sub_id: subId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param orgId
   * @param subscriptionId
   * @returns UsageSummary
   * @throws ApiError
   */
  public static getOrganizationSubscriptionUsage(
    orgId: string,
    subscriptionId: string,
  ): CancelablePromise<UsageSummary> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/organizations/{org_id}/subscriptions/{subscription_id}/usage',
      path: {
        org_id: orgId,
        subscription_id: subscriptionId,
      },
    });
  }

  /**
   * @param orgId
   * @param planId
   * @param seats
   * @param promoCode
   * @returns TaxDetails
   * @throws ApiError
   */
  public static getOrganizationPlanTax(
    orgId: string,
    planId: string,
    seats: number,
    promoCode?: string,
  ): CancelablePromise<TaxDetails> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/organizations/{org_id}/plans/{plan_id}/tax',
      path: {
        org_id: orgId,
        plan_id: planId,
      },
      query: {
        seats: seats,
        promo_code: promoCode,
      },
    });
  }

  /**
   * @param orgId
   * @returns LicenseAssignment
   * @throws ApiError
   */
  public static listOrganizationLicenseAssignments(orgId: any): CancelablePromise<Array<LicenseAssignment>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/organizations/{org_id}/license-assignments',
      path: {
        org_id: orgId,
      },
    });
  }

  /**
   * @param orgId
   * @param requestBody
   * @returns LicenseAssignment
   * @throws ApiError
   */
  public static assignLicense(
    orgId: any,
    requestBody?: {
      user_id: string;
      subscription_id?: string;
    },
  ): CancelablePromise<LicenseAssignment> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/organizations/{org_id}/license-assignments',
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param orgId
   * @returns CreditCard
   * @throws ApiError
   */
  public static listOrganizationCards(orgId: any): CancelablePromise<Array<CreditCard>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/organizations/{org_id}/cards',
      path: {
        org_id: orgId,
      },
    });
  }

  /**
   * @param orgId
   * @param requestBody
   * @returns CreditCard
   * @throws ApiError
   */
  public static attachOrganizationCard(
    orgId: any,
    requestBody?: {
      token?: string;
      currency?: string;
    },
  ): CancelablePromise<CreditCard> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/organizations/{org_id}/cards',
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param orgId
   * @param cardId
   * @param currency
   * @returns CreditCard
   * @throws ApiError
   */
  public static removeOrganizationCard(orgId: any, cardId: any, currency?: any): CancelablePromise<CreditCard> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/v1/admin/organizations/{org_id}/cards/{card_id}',
      path: {
        org_id: orgId,
        card_id: cardId,
      },
      query: {
        currency: currency,
      },
    });
  }

  /**
   * @param orgId
   * @param cardId
   * @param currency
   * @returns CreditCard
   * @throws ApiError
   */
  public static changeOrganizationCard(orgId: any, cardId: any, currency: any): CancelablePromise<CreditCard> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/organizations/{org_id}/cards/{card_id}/change',
      path: {
        org_id: orgId,
        card_id: cardId,
      },
      query: {
        currency: currency,
      },
    });
  }

  /**
   * @param query
   * @returns AdminSearchResult
   * @throws ApiError
   */
  public static getSearchResults(query: any): CancelablePromise<AdminSearchResult> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/search',
      query: {
        query: query,
      },
    });
  }

  /**
   * @param requestBody
   * @returns any OK
   * @throws ApiError
   */
  public static sendAnnouncement(requestBody?: { rich_text: string }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/announcement/send',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param requestBody
   * @returns any
   * @throws ApiError
   */
  public static generateAnnouncementPreview(requestBody?: { rich_text: string }): CancelablePromise<{
    preview_html: string;
  }> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/announcement/preview',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param orgId
   * @param userId
   * @param requestBody
   * @returns LicenseAssignment
   * @throws ApiError
   */
  public static setLicenseAssignments(
    orgId: any,
    userId: any,
    requestBody?: SetLicenseAssignmentsRequest,
  ): CancelablePromise<Array<LicenseAssignment>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/v1/admin/organizations/{org_id}/license-assignments/{user_id}',
      path: {
        org_id: orgId,
        user_id: userId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param orgId
   * @param assignmentId
   * @returns LicenseAssignment
   * @throws ApiError
   */
  public static unassignLicense(orgId: any, assignmentId: any): CancelablePromise<LicenseAssignment> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/v1/admin/organizations/{org_id}/license-assignments/{assignment_id}',
      path: {
        org_id: orgId,
        assignment_id: assignmentId,
      },
    });
  }

  /**
   * @param userId
   * @returns LegalAcceptance
   * @throws ApiError
   */
  public static listUserLegalAcceptances(userId: any): CancelablePromise<Array<LegalAcceptance>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/users/{user_id}/legal/acceptance',
      path: {
        user_id: userId,
      },
    });
  }

  /**
   * @param orgId
   * @param cursor Opaque pagination cursor returned by a previous response.
   * @returns any
   * @throws ApiError
   */
  public static listIdentityEvents(
    orgId: any,
    cursor?: string,
  ): CancelablePromise<{
    items?: Array<IdentityLogEvent>;
    next_cursor?: string | null;
  }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/organizations/{org_id}/events',
      path: {
        org_id: orgId,
      },
      query: {
        cursor: cursor,
      },
    });
  }

  /**
   * @param orgId
   * @param subscriptionId
   * @returns any
   * @throws ApiError
   */
  public static listSubscriptionFeatures(
    orgId: any,
    subscriptionId: any,
  ): CancelablePromise<{
    default: Array<Array<string>>;
    add: Array<Array<string>>;
    remove: Array<Array<string>>;
  }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/v1/admin/organizations/{org_id}/subscriptions/{subscription_id}/features',
      path: {
        org_id: orgId,
        subscription_id: subscriptionId,
      },
    });
  }

  /**
   * @param orgId
   * @param subscriptionId
   * @param requestBody
   * @returns string
   * @throws ApiError
   */
  public static updateSubscriptionFeatures(
    orgId: any,
    subscriptionId: any,
    requestBody?: {
      add?: Array<Array<string>>;
      remove?: Array<Array<string>>;
    },
  ): CancelablePromise<Array<Array<string>>> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/v1/admin/organizations/{org_id}/subscriptions/{subscription_id}/features',
      path: {
        org_id: orgId,
        subscription_id: subscriptionId,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }
}
